import { Grid, Stack, Tooltip, Typography } from "@mui/material";
import Button from "e.sic-platform.webclient/src/components/button/Button";
import { useTheme } from "e.sic-platform.webclient/src/services/theme-service/ThemeService";
import { translationService } from "e.sic-platform.webclient/src/services/translation/TranslationService";
import { FILE_EXPIRATION_DAYS, shopHistoryStorage } from "e.sic-platform.webclient/src/services/shop/ShopHistoryStorage";
import { fileExpirationStorage } from "e.sic-platform.webclient/src/services/shop/FileExpirationStorage";
import { dateService } from "e.sic-platform.webclient/src/services/date-service/DateService";
import { observer } from "mobx-react-lite";
import { useCallback, useEffect, useState } from "react";

export default observer(function CopyUrlButton(props: {id?: number, disabled?: boolean}) {
    const {themeClass} = useTheme();
    const [text, setText] = useState("general.copyUrl.copy");
    const copyUrl = useCallback(() => {
        if (props.disabled || props.id === undefined) return;
        var url = window.location.href;
        if (!url.includes("?guid=")) url += `?guid=${props.id}`;
        navigator.clipboard.writeText(url);
        setText("general.copyUrl.copied");
    }, [props.id, props.disabled])

    // Ask the server for THIS file's dates. No list can answer it: the shared history store is
    // filtered to running files and to one page of rows, so a finished file — the only kind
    // anyone shares a link to — is normally absent from it.
    useEffect(() => {
        if (props.id === undefined) return;
        void fileExpirationStorage.loadForFile(props.id);
    }, [props.id]);

    // file.Created + the retention window, computed server-side by the same rule that deletes
    // the results. The history row is the stand-in until that request lands (its own expiration
    // is derived from the file's creation date too, so the two agree).
    const expiresAt = fileExpirationStorage.getExpirationForFile(props.id)
        ?? shopHistoryStorage.getExpirationForFile(props.id);
    // Days that are actually left: retention minus the age of the file, not a fresh window on
    // every visit.
    const daysLeft = expiresAt
        ? Math.max(0, Math.ceil((expiresAt.getTime() - Date.now()) / 86400000))
        : undefined;
    // Only a file we can date at all gets the exact wording; otherwise state the policy.
    const expiryText = expiresAt
        ? translationService
              .translate("general.copyUrl.expiresOn")
              .replace("{0}", String(daysLeft))
              .replace("{1}", dateService.formatDateTime(expiresAt))
        : translationService
              .translate("general.copyUrl.expiresInDays")
              .replace("{0}", String(FILE_EXPIRATION_DAYS));

    return (
        <Grid container xs={12} sm={12} className='centered'>
            <Stack alignItems="center">
                <Button
                    id={"copy-url"}
                    className={themeClass(["btn-big"])}
                    textKey={text}
                    onClick={copyUrl}
                    disabled={props.disabled}
                />
                {/* Anyone copying this link needs to know it stops working — say so next to the
                    button rather than letting them find out when it 404s for the recipient. */}
                {props.id !== undefined && (
                    <Tooltip title={translationService.translate("general.copyUrl.expiryHint")}>
                        <Typography
                            variant="caption"
                            color={daysLeft !== undefined && daysLeft <= 1 ? "error" : "text.secondary"}
                            sx={{ mt: 0.5, textAlign: "center", whiteSpace: "nowrap" }}
                        >
                            {expiryText}
                        </Typography>
                    </Tooltip>
                )}
            </Stack>
        </Grid>
    )
})
